<template>
  <a-row>
    <!-- 搜索区域 -->
    <a-row style="margin-bottom: 16px;">
      <a-col span="4">
        <a-input
          v-model="queryForm.title"
          placeholder="新闻标题"
          allowClear
        />
      </a-col>
      <a-col span="4" style="padding-left: 8px;">
        <a-select
          v-model="queryForm.categoryId"
          placeholder="新闻分类"
          allowClear
          style="width: 100%"
        >
          <a-select-option :value="1">通知</a-select-option>
          <a-select-option :value="2">动态</a-select-option>
          <a-select-option :value="3">政策</a-select-option>
        </a-select>
      </a-col>
      <a-col span="4" style="padding-left: 8px;">
        <a-select
          v-model="queryForm.status"
          placeholder="新闻状态"
          allowClear
          style="width: 100%"
        >
          <a-select-option :value="0">草稿</a-select-option>
          <a-select-option :value="1">已发布</a-select-option>
        </a-select>
      </a-col>
      <a-col span="12" style="padding-left: 8px; text-align: right;">
        <a-button type="primary" @click="handleSearch" :loading="loading">
          <a-icon type="search" /> 搜索
        </a-button>
        <a-button style="margin-left: 8px;" @click="handleReset">
          <a-icon type="reload" /> 重置
        </a-button>
        <CommonPermissions :p="$permissions.NEWS.CREATE">
          <a-button type="primary" style="margin-left: 8px;" @click="handleCreate">
            <a-icon type="plus" /> 新建
          </a-button>
        </CommonPermissions>
      </a-col>
    </a-row>

    <!-- 数据表格 -->
    <a-table
      :columns="columns"
      :data-source="data"
      :pagination="{
        current: pagination.current,
        total: pagination.total,
        pageSize: pagination.pageSize,
        showSizeChanger: false,
        showTotal: (total) => `共 ${total} 条`
      }"
      :loading="loading"
      row-key="id"
      @change="handleTableChange"
    >
      <!-- 封面图片列 -->
      <div slot="coverImageUrl" slot-scope="text, record">
        <img 
          v-if="record && record.coverUrl" 
          :src="record.coverUrl" 
          style="width: 80px; height: 60px; object-fit: cover; border-radius: 4px;"
          @error="handleImageError"
        />
        <div v-else style="width: 80px; height: 60px; background-color: #f5f5f5; border-radius: 4px; display: flex; align-items: center; justify-content: center; color: #999; font-size: 12px;">
          无封面
        </div>
      </div>
      
      <!-- 标题列 -->
      <div slot="title" slot-scope="text, record">
        <div v-if="record" style="font-weight: bold; margin-bottom: 4px;">{{ record.title || '-' }}</div>
        <div v-if="record && record.summary" style="font-size: 12px; color: #666; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 300px;">
          {{ record.summary }}
        </div>
      </div>
      
      <!-- 分类列 -->
      <span slot="category" slot-scope="text, record">
        <template v-if="record">
          <a-tag v-if="record.categoryId === 1" color="blue">通知</a-tag>
          <a-tag v-else-if="record.categoryId === 2" color="green">动态</a-tag>
          <a-tag v-else-if="record.categoryId === 3" color="orange">政策</a-tag>
          <a-tag v-else color="default">未知</a-tag>
        </template>
        <span v-else>-</span>
      </span>
      
      <!-- 状态列 -->
      <span slot="status" slot-scope="text, record">
        <template v-if="record">
          <a-tag v-if="record.status === 0">草稿</a-tag>
          <a-tag v-else-if="record.status === 1" color="green">已发布</a-tag>
          <a-tag v-else color="default">未知</a-tag>
        </template>
        <span v-else>-</span>
      </span>
      
      <div slot="timeBlock" slot-scope="text, record">
        <div v-if="record" class="news-meta-col">
          <div class="news-meta-line news-meta-line--time">
            <span class="news-meta-k">创建时间</span>
            <span class="news-meta-v">{{
              record.gmtCreate ? $moment(record.gmtCreate).format('YYYY-MM-DD HH:mm:ss') : '—'
            }}</span>
          </div>
          <div class="news-meta-line news-meta-line--time">
            <span class="news-meta-k">修改时间</span>
            <span class="news-meta-v">{{
              record.gmtModified ? $moment(record.gmtModified).format('YYYY-MM-DD HH:mm:ss') : '—'
            }}</span>
          </div>
          <div class="news-meta-line news-meta-line--time">
            <span class="news-meta-k">发布时间</span>
            <span class="news-meta-v">{{
              record.publishTime ? $moment(record.publishTime).format('YYYY-MM-DD HH:mm:ss') : '—'
            }}</span>
          </div>
        </div>
        <span v-else>—</span>
      </div>
      
      <!-- 操作列 -->
      <span slot="action" slot-scope="text, record">
        <template v-if="record">
          <CommonPermissions :p="$permissions.NEWS.RETRIEVE">
            <a @click="() => viewNews(record)">查看</a>
          </CommonPermissions>
          <CommonPermissions :p="$permissions.NEWS.RETRIEVE">
            <a-divider type="vertical" />
            <a :disabled="previewLoading" @click="() => openNewsPreview(record)">预览</a>
          </CommonPermissions>
          <CommonPermissions :p="$permissions.NEWS.UPDATE">
            <a-divider type="vertical" />
            <a @click="() => editNews(record)">编辑</a>
          </CommonPermissions>
          <CommonPermissions :p="$permissions.NEWS.PUBLISH">
            <a-divider type="vertical" />
            <a
              v-if="record.status === 0"
              @click="() => publishNews(record)"
              style="color: #52c41a;"
            >
              发布
            </a>
            <a
              v-else-if="record.status === 1"
              @click="() => withdrawNews(record)"
              style="color: #ff9800;"
            >
              撤回
            </a>
          </CommonPermissions>
          <CommonPermissions :p="$permissions.NEWS.DELETE">
            <a-divider type="vertical" />
            <a-popconfirm
              title="确定要删除这条新闻吗？"
              @confirm="() => deleteNews(record)"
            >
              <a style="color: #ff4d4f;">删除</a>
            </a-popconfirm>
          </CommonPermissions>
        </template>
      </span>
    </a-table>

    <!-- 新闻详情抽屉 -->
    <a-drawer
      title="新闻详情"
      placement="right"
      width="60%"
      :closable="false"
      :visible="showDetailDrawer"
      @close="() => triggerDetailDrawer()"
    >
      <div v-if="currentNews">
        <a-descriptions :column="1" bordered>
          <a-descriptions-item label="新闻ID">{{ currentNews.id }}</a-descriptions-item>
          <a-descriptions-item label="标题">{{ currentNews.title || '-' }}</a-descriptions-item>
          <a-descriptions-item label="摘要">{{ currentNews.summary || '-' }}</a-descriptions-item>
          <a-descriptions-item label="分类">
            <a-tag v-if="currentNews.categoryId === 1" color="blue">通知</a-tag>
            <a-tag v-else-if="currentNews.categoryId === 2" color="green">动态</a-tag>
            <a-tag v-else-if="currentNews.categoryId === 3" color="orange">政策</a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="状态">
            <a-tag v-if="currentNews.status === 0">草稿</a-tag>
            <a-tag v-else-if="currentNews.status === 1" color="green">已发布</a-tag>
            <a-tag v-else color="default">未知</a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="封面图片" v-if="currentNews.coverImageUrl">
            <img 
              :src="currentNews.coverImageUrl" 
              style="max-width: 300px; max-height: 200px; border-radius: 4px;"
              @error="handleImageError"
            />
          </a-descriptions-item>
          <a-descriptions-item label="发布时间">
            {{ currentNews.publishTime ? $moment(currentNews.publishTime).format('YYYY-MM-DD HH:mm:ss') : '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="创建时间">
            {{ currentNews.gmtCreate ? $moment(currentNews.gmtCreate).format('YYYY-MM-DD HH:mm:ss') : '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="修改时间">
            {{ currentNews.gmtModified ? $moment(currentNews.gmtModified).format('YYYY-MM-DD HH:mm:ss') : '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="内容">
            <div 
              v-html="currentNews.content" 
              style="max-height: 400px; overflow-y: auto; border: 1px solid #f0f0f0; padding: 12px; border-radius: 4px;"
            ></div>
          </a-descriptions-item>
        </a-descriptions>
        
        <div style="margin-top: 24px; text-align: right;">
          <a-button @click="() => triggerDetailDrawer()">关闭</a-button>
        </div>
      </div>
    </a-drawer>
  </a-row>
</template>

<script>
import { mapState } from 'vuex'
import NewsManageApi from '@/api/src/news/api/NewsManageApi'
import CommonPermissions from '@/components/CommonPermissions'

const newsManageApi = new NewsManageApi()

/** 前台 H5 新闻页域名（预览页路径 /news/{id}，签名为 query: id、m、s） */
const NEWS_PREVIEW_H5_BASE = 'https://uestcp.fmyundianji.com'

const columns = [
  {
    title: '封面',
    dataIndex: 'coverImageUrl',
    key: 'coverImageUrl',
    scopedSlots: { customRender: 'coverImageUrl' },
  },
  {
    title: '标题',
    key: 'title',
    scopedSlots: { customRender: 'title' },
  },
  {
    title: '分类',
    dataIndex: 'category',
    key: 'category',
    scopedSlots: { customRender: 'category' },
  },
  {
    title: '状态',
    dataIndex: 'status',
    key: 'status',
    scopedSlots: { customRender: 'status' },
  },
  // {
  //   title: '作者',
  //   dataIndex: 'author',
  //   key: 'author',
  //   width: 120,
  // },
  // {
  //   title: '浏览次数',
  //   dataIndex: 'viewCount',
  //   key: 'viewCount',
  //   width: 100,
  // },
  {
    title: '创建 / 修改 / 发布时间',
    key: 'timeBlock',
    width: 220,
    scopedSlots: { customRender: 'timeBlock' },
  },
  {
    title: '操作',
    key: 'action',
    width: 320,
    scopedSlots: { customRender: 'action' },
  },
]

export default {
  name: 'News',
  components: {
    CommonPermissions,
  },
  data() {
    return {
      columns: columns,
      data: [],
      loading: false,
      showDetailDrawer: false,
      currentNews: null,
      queryForm: {
        title: '',
        categoryId: undefined,
        status: undefined
      },
      pagination: {
        current: 1,
        total: 0,
        pageSize: 10
      },
      previewLoading: false
    }
  },
  computed: mapState({
  }),
  async mounted() {
    await this.fetchNewsList(1)
  },
  methods: {
    // 获取新闻列表
    async fetchNewsList(page) {
      this.loading = true
      try {
        const opts = {
          page: page,
          pageSize: this.pagination.pageSize,
          title: this.queryForm.title || undefined,
          categoryId: this.queryForm.categoryId,
          status: this.queryForm.status
        }
        
        const resp = await newsManageApi.listNewsForAdmin(opts)
        console.log('新闻列表:', resp)
        
        this.data = (resp && resp.list) ? resp.list : []
        // 注意：PagingNewsAdminVo 使用 totalCount 而不是 total
        this.pagination.total = (resp && resp.totalCount) ? resp.totalCount : 0
        this.pagination.current = page
      } catch (error) {
        console.error('获取新闻列表失败:', error)
        this.$message.error('获取新闻列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 搜索
    async handleSearch() {
      this.pagination.current = 1
      await this.fetchNewsList(1)
    },
    
    // 重置
    handleReset() {
      this.queryForm = {
        title: '',
        categoryId: undefined,
        status: undefined
      }
      this.pagination.current = 1
      this.fetchNewsList(1)
    },
    
    // 表格变化
    async handleTableChange(pagination) {
      console.log('表格变化:', pagination)
      await this.fetchNewsList(pagination.current)
    },
    
    // 查看新闻详情
    async viewNews(record) {
      try {
        const resp = await newsManageApi.getNewsDetailForAdmin(record.id)
        console.log('新闻详情:', resp)
        this.currentNews = resp
        this.showDetailDrawer = true
      } catch (error) {
        console.error('获取新闻详情失败:', error)
        this.$message.error('获取新闻详情失败')
      }
    },
    
    /**
     * 打开 H5 新闻预览新窗口（接口返回 id / modifiedMillis / signature，拼为 query：id、m、s）
     */
    async openNewsPreview(record) {
      if (!record || record.id == null) return
      this.previewLoading = true
      try {
        const vo = await newsManageApi.createNewsPreviewUrl(record.id)
        const pathId = vo && vo.id != null ? vo.id : record.id
        const search = new URLSearchParams()
        if (vo && vo.modifiedMillis != null) search.set('m', String(vo.modifiedMillis))
        if (vo && vo.signature) search.set('s', String(vo.signature))
        const q = search.toString()
        const url = `${NEWS_PREVIEW_H5_BASE}/news/${pathId}${q ? `?${q}` : ''}`
        window.open(url, '_blank', 'noopener,noreferrer')
      } catch (error) {
        console.error('生成新闻预览地址失败:', error)
        this.$message.error((error && error.message) || '生成预览地址失败')
      } finally {
        this.previewLoading = false
      }
    },

    // 编辑新闻
    editNews(record) {
      this.$router.push({
        name: 'NewsEditor',
        query: { id: record.id }
      })
    },
    
    // 新建新闻
    handleCreate() {
      this.$router.push({
        name: 'NewsEditor'
      })
    },
    
    // 发布新闻
    async publishNews(record) {
      try {
        await newsManageApi.publishNews(record.id)
        this.$message.success('发布成功')
        await this.fetchNewsList(this.pagination.current)
      } catch (error) {
        console.error('发布新闻失败:', error)
        this.$message.error('发布新闻失败')
      }
    },
    
    // 撤回新闻
    async withdrawNews(record) {
      try {
        await newsManageApi.withdrawNews(record.id)
        this.$message.success('撤回成功')
        await this.fetchNewsList(this.pagination.current)
      } catch (error) {
        console.error('撤回新闻失败:', error)
        this.$message.error('撤回新闻失败')
      }
    },
    
    // 删除新闻
    async deleteNews(record) {
      try {
        await newsManageApi.deleteNews(record.id)
        this.$message.success('删除成功')
        await this.fetchNewsList(this.pagination.current)
      } catch (error) {
        console.error('删除新闻失败:', error)
        this.$message.error('删除新闻失败')
      }
    },
    
    // 控制详情抽屉显示
    triggerDetailDrawer() {
      this.showDetailDrawer = !this.showDetailDrawer
      if (!this.showDetailDrawer) {
        this.currentNews = null
      }
    },
    
    // 图片加载错误处理
    handleImageError(event) {
      event.target.style.display = 'none'
    }
  }
}
</script>

<style scoped lang="less">
.ant-table-wrapper {
  margin-top: 16px;
}

.news-meta-col {
  min-width: 0;
  line-height: 1.5;
}

.news-meta-line {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;

  & + & {
    margin-top: 4px;
  }
}

.news-meta-k {
  flex: none;
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
}

.news-meta-v {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.news-meta-line--time {
  align-items: center;
}
</style>

